import React, { useMemo, useState, useEffect } from "react";
import { PriceAlertEventData } from "@/api/price-alerts";
import {
  useTokenLivePrice,
  type LivePriceExchangeCode,
} from "@/hooks/useTokenLivePrice";
import { useInView } from "@/hooks/useInView";
import { ensureUsdtSymbol } from "@/utils/symbols";
import { Sparkline } from "@/components/ui/Sparkline";

const mapAlertExchangeToLiveCode = (
  exchange: string | undefined
): LivePriceExchangeCode => {
  const code = (exchange ?? "").toLowerCase();
  if (code.includes("binance")) return "binance";
  if (code.includes("bybit")) return "bybit";
  if (code.includes("gate")) return "gate";
  if (code.includes("kucoin")) return "kucoin";
  if (code.includes("aster")) return "asterdex";
  if (code.includes("hyperliquid")) return "hyperliquid";
  return "dynamic";
};

interface PriceAlertEventTileProps {
  event: PriceAlertEventData;
  onAnalyze?: (event: PriceAlertEventData) => void;
  onTrade?: (event: PriceAlertEventData) => void;
}

const formatPrice = (price: number) =>
  `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}`;

const formatTimeAgo = (dateStr: string) => {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diffMs / 60_000);
  const hours = Math.floor(diffMs / 3_600_000);
  const days = Math.floor(diffMs / 86_400_000);

  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
};

const formatDateTime = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

export const PriceAlertEventTile: React.FC<PriceAlertEventTileProps> = ({
  event,
  onAnalyze,
  onTrade,
}) => {
  const isSpike = event.alertType.toLowerCase() === "spike";
  const sign = event.changePercent >= 0 ? "+" : "";

  const [tileRef, isInView] = useInView<HTMLDivElement>({
    rootMargin: "50px",
    threshold: 0.1,
  });

  const normalizedSymbol = useMemo(
    () => ensureUsdtSymbol(event.symbol),
    [event.symbol]
  );

  const liveExchangeCode = useMemo(
    () => mapAlertExchangeToLiveCode(event.exchange),
    [event.exchange]
  );

  const { price: livePrice, isSubscribed } = useTokenLivePrice(
    normalizedSymbol,
    liveExchangeCode,
    isInView
  );

  const [priceLoadTimeout, setPriceLoadTimeout] = useState(false);

  useEffect(() => {
    if (isInView && livePrice == null) {
      setPriceLoadTimeout(false);
      const timer = setTimeout(() => {
        setPriceLoadTimeout(true);
      }, 8000);

      return () => clearTimeout(timer);
    }
  }, [isInView, livePrice]);

  const isPriceLoading = isInView && livePrice == null && !priceLoadTimeout;

  const liveChangePercent = useMemo(() => {
    if (livePrice == null || event.startPrice === 0) return null;
    return ((livePrice - event.startPrice) / event.startPrice) * 100;
  }, [livePrice, event.startPrice]);

  const liveChangeSign =
    liveChangePercent != null && liveChangePercent >= 0 ? "+" : "";

  const liveChangeColorClass = useMemo(() => {
    if (liveChangePercent == null) return "text-text-secondary";
    return liveChangePercent >= 0 ? "text-green-400" : "text-red-400";
  }, [liveChangePercent]);

  const livePulse = isSubscribed ? (
    <span className="relative inline-flex h-2 w-2 shrink-0" aria-hidden>
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400/70" />
      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
    </span>
  ) : null;

  const liveChangeValues =
    liveChangePercent == null || livePrice == null ? (
      <span className="text-text-secondary">—</span>
    ) : (
      <span className="inline-flex items-center gap-2 tabular-nums">
        <span className="text-sm font-semibold text-white">
          {formatPrice(livePrice)}
        </span>
        <span
          className={`font-semibold text-sm lg:text-xs ${liveChangeColorClass} ${
            isSubscribed ? "transition-colors duration-150" : ""
          }`}
        >
          {liveChangeSign}
          {liveChangePercent.toFixed(2)}%
        </span>
      </span>
    );

  const liveStripValues = isPriceLoading ? (
    <div className="inline-flex items-center gap-2 tabular-nums">
      <div className="h-4 bg-gray-600/30 rounded animate-pulse w-[4.5rem]" />
      <div className="h-4 bg-gray-600/30 rounded animate-pulse w-12" />
    </div>
  ) : (
    liveChangeValues
  );

  const sparkline = event.sparkline;

  const exchangeName = (event.exchange ?? "")
    .split("_")
    .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : part))
    .join(" ");
  const changeClass = event.changePercent >= 0 ? "text-[#4ade80]" : "text-[#f87171]";

  return (
    <div ref={tileRef} className="glass flex flex-col gap-3 p-3">
      {/* Symbol, type, exchange; alert move on the right */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <h3 className="text-base font-semibold leading-6 text-white">{event.symbol}</h3>
            <span
              className={`rounded-full border px-1.5 text-[10px] leading-4 ${
                isSpike
                  ? "border-[#00653b] bg-[#002c14] text-[#56d799]"
                  : "border-[#7f1d1d] bg-[#2a0a0a] text-[#f87171]"
              }`}
            >
              {isSpike ? "Spike" : "Drop"}
            </span>
          </div>
          <p className="mt-0.5 text-xs text-[#a3a3a3]">
            {exchangeName} · {formatTimeAgo(event.timestamp)}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-xs text-[#a3a3a3]">Change</p>
          <p className={`text-base font-semibold tabular-nums ${changeClass}`}>
            {sign}
            {event.changePercent.toFixed(2)}%
          </p>
        </div>
      </div>

      {/* Alert facts */}
      <div className="grid grid-cols-4 gap-2 border-t border-white/10 pt-3">
        <div className="min-w-0">
          <p className="text-[10px] text-[#a3a3a3]">Start Price</p>
          <p className="truncate text-xs font-semibold tabular-nums text-white">
            {formatPrice(event.startPrice)}
          </p>
        </div>
        <div className="min-w-0">
          <p className="text-[10px] text-[#a3a3a3]">Alert Price</p>
          <p className="truncate text-xs font-semibold tabular-nums text-white">
            {formatPrice(event.currentPrice)}
          </p>
        </div>
        <div className="min-w-0">
          <p className="text-[10px] text-[#a3a3a3]">Timeframe</p>
          <p className="text-xs font-semibold text-white">{event.interval}</p>
        </div>
        <div className="min-w-0">
          <p className="text-[10px] text-[#a3a3a3]">Date & Time</p>
          <p className="truncate text-xs font-semibold text-white">
            {formatDateTime(event.timestamp)}
          </p>
        </div>
      </div>

      {/* Live price since the alert started */}
      <div className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-[#0a0a0a]/60 px-3 py-2.5">
        <div className="flex items-center gap-2">
          {livePulse ?? <span className="h-2 w-2 rounded-full bg-[#f87171]" aria-hidden />}
          <span className="text-xs text-[#a3a3a3]">Live Price</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm font-semibold tabular-nums">{liveStripValues}</div>
          {sparkline && sparkline.length > 1 && (
            <Sparkline values={sparkline} width={64} height={22} />
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onAnalyze?.(event);
          }}
          className="flex-1 rounded-full border border-white/15 bg-[#171717] px-4 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-[#262626]"
        >
          Analyze
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onTrade?.(event);
          }}
          className="flex-1 rounded-full bg-accent px-4 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-accent-hover"
        >
          Trade
        </button>
      </div>
    </div>
  );
};
